import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage, LANGUAGES } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Radio,
  Building2,
  Users,
  FileBarChart,
  AlertTriangle,
  UserX,
  CalendarCheck,
  Calendar,
  CalendarDays,
  Settings,
  ClipboardList,
  CreditCard,
  DollarSign,
  Shield,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  TrendingUp,
  MessageSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';

export default function Sidebar({ onClose }) {
  const { user, logout } = useAuth();
  const { t, language, setLanguage, languages } = useLanguage();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const currentLang = languages.find(l => l.code === language) || languages[0];

  const navItems = [
    { path: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard, roles: ['admin', 'hr', 'controller', 'employee'] },
    { path: '/my-schedule', labelKey: 'nav.mySchedule', icon: CalendarDays, roles: ['employee'] },
    { path: '/my-objects', labelKey: 'nav.myObjects', icon: Building2, roles: ['employee'] },
    { path: '/my-history', labelKey: 'nav.myHistory', icon: TrendingUp, roles: ['employee'] },
    { path: '/my-notes', labelKey: 'nav.myNotes', icon: MessageSquare, roles: ['employee'] },
    { path: '/live', labelKey: 'nav.liveView', icon: Radio, roles: ['admin', 'hr', 'controller'] },
    { path: '/objects', labelKey: 'nav.objects', icon: Building2, roles: ['admin'] },
    { path: '/employees', labelKey: 'nav.employees', icon: Users, roles: ['admin', 'hr'] },
    { path: '/reports', labelKey: 'nav.reports', icon: FileBarChart, roles: ['admin', 'hr', 'controller'] },
    { path: '/violations', labelKey: 'nav.violations', icon: AlertTriangle, roles: ['admin', 'hr', 'controller'] },
    { path: '/absences', labelKey: 'nav.absences', icon: UserX, roles: ['admin', 'hr', 'controller'] },
    { path: '/months', labelKey: 'nav.monthClosing', icon: CalendarCheck, roles: ['admin', 'hr'] },
    { path: '/schedule', labelKey: 'nav.schedule', icon: Calendar, roles: ['admin', 'hr'] },
    { path: '/audit', labelKey: 'nav.audit', icon: ClipboardList, roles: ['admin'] },
    { path: '/billing', labelKey: 'nav.billing', icon: CreditCard, roles: ['admin', 'super_admin'] },
    { path: '/salaries', labelKey: 'nav.salaries', icon: DollarSign, roles: ['admin'] },
    { path: '/super-admin', labelKey: 'nav.superAdmin', icon: Shield, roles: ['super_admin'] },
    { path: '/settings', labelKey: 'nav.settings', icon: Settings, roles: ['admin', 'super_admin'] },
  ];

  const filteredItems = navItems.filter(item => item.roles.includes(user?.role));

  const handleNavClick = () => {
    if (onClose) onClose();
  };

  return (
    <aside 
      className={cn(
        "flex flex-col h-screen bg-wp-darker border-r border-wp-dark/30 transition-all duration-300",
        collapsed ? "w-20" : "w-64 md:w-72"
      )}
      data-testid="sidebar"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-5 border-b border-wp-dark/30">
        {!collapsed && (
          <Link to="/dashboard" className="flex items-center" onClick={handleNavClick}>
            <span className="text-2xl font-light tracking-wide text-white font-heading">WORK</span>
            <span className="text-2xl font-light tracking-wide text-wp-light font-heading">+</span>
          </Link>
        )}
        {collapsed && (
          <Link to="/dashboard" className="flex items-center mx-auto" onClick={handleNavClick}>
            <span className="text-xl font-light tracking-wide text-white font-heading">W</span>
            <span className="text-xl font-light tracking-wide text-wp-light font-heading">+</span>
          </Link>
        )}
        <div className="flex items-center gap-1">
          {/* Desktop collapse toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className={cn("h-8 w-8 text-wp-light/50 hover:text-wp-lightest hover:bg-wp-dark/30 hidden md:flex", collapsed && "mx-auto mt-3")}
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
          {/* Mobile close button */}
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8 text-wp-light/50 hover:text-wp-lightest hover:bg-wp-dark/30 md:hidden"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-2">
        <ul className="space-y-0.5">
          {filteredItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  onClick={handleNavClick}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 md:py-2.5 rounded-xl transition-all duration-200",
                    isActive 
                      ? "bg-wp-medium/20 text-wp-lightest border-l-2 border-wp-light" 
                      : "text-wp-light/70 hover:text-wp-lightest hover:bg-wp-dark/30",
                    collapsed && "justify-center px-3"
                  )}
                  data-testid={`nav-${item.path.slice(1)}`}
                  title={collapsed ? t(item.labelKey) : undefined}
                >
                  <Icon className={cn("h-5 w-5 flex-shrink-0", isActive && "drop-shadow-[0_0_8px_rgba(192,230,253,0.5)]")} />
                  {!collapsed && <span className="font-medium text-sm">{t(item.labelKey)}</span>}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Bottom controls */}
      <div className="px-3 py-2 border-t border-wp-dark/30 mt-auto bg-wp-darker/95 backdrop-blur-sm">
        {!collapsed && (
          <Button
            variant="ghost"
            onClick={logout}
            className="w-full justify-start text-rose-300 hover:text-rose-200 hover:bg-rose-500/10 rounded-xl border border-rose-500/20 min-h-11 mb-3"
            data-testid="logout-button-mobile-visible"
          >
            <LogOut className="h-5 w-5" />
            <span className="ml-3">{t('auth.logout')}</span>
          </Button>
        )}

        {!collapsed ? (
          <div className="space-y-2">
            {languages.map((lang) => (
              <Button
                key={lang.code}
                type="button"
                variant="ghost"
                onClick={() => setLanguage(lang.code)}
                className={cn(
                  "w-full justify-start text-wp-light/70 hover:text-wp-lightest hover:bg-wp-dark/30 rounded-xl gap-3 min-h-11",
                  language === lang.code && "text-wp-lightest bg-wp-medium/20 border border-wp-light/20"
                )}
                data-testid={`lang-${lang.code}`}
              >
                <span className="text-lg">{lang.flag}</span>
                <span className="font-medium">{lang.name}</span>
                {language === lang.code && (
                  <Check className="h-4 w-4 ml-auto text-wp-lightest" />
                )}
              </Button>
            ))}
          </div>
        ) : (
          <Button
            variant="ghost"
            className="w-full justify-center px-3 text-wp-light/70 hover:text-wp-lightest hover:bg-wp-dark/30 rounded-xl"
            data-testid="language-toggle"
            title={currentLang.name}
          >
            <span className="text-lg">{currentLang.flag}</span>
          </Button>
        )}
      </div>

      {/* User section */}
      <div className="p-4 border-t border-wp-dark/30" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}>
        {!collapsed && (
          <div className="space-y-3">
            <div className="p-3 rounded-xl bg-wp-dark/30">
            <p className="font-medium text-sm text-wp-lightest truncate">{user?.name}</p>
            <p className="text-xs text-wp-light/50 truncate">{user?.email}</p>
            </div>
          </div>
        )}
        {collapsed && (
          <Button
            variant="ghost"
            onClick={logout}
            className="w-full justify-center px-3 text-wp-light/50 hover:text-rose-400 hover:bg-rose-500/10 rounded-xl"
            data-testid="logout-button"
          >
            <LogOut className="h-5 w-5" />
          </Button>
        )}
      </div>
    </aside>
  );
}
